import TextField from "../../ui/TextField";
import React, { useState, useEffect, useRef } from "react";
import { Modal, Box, IconButton, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { apiTomaInventarioBuscarDetalle } from "../../../settings/ApiConfig";
import { useLayout } from "../../../context/System/LayoutContext";
import { useRequest } from "../../../context/System/RequestContext";

const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '75vw',
    maxWidth: '900px',
    height: '75vh',
    bgcolor: 'background.paper',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    borderRadius: '8px',
    p: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
};

export default function ModalBuscarArticulosTomaInventario({
    openModal,
    handleCloseModal,
    handleArticuloSelect,
    folioInventario,
    // Ref opcional desde el padre para el Alt+F1 global (repetir Alt+F1
    // enfoca el filtro aunque el foco esté en el lector o la tabla).
    inputRefExterno,
}) {
    const { GetRequest } = useRequest();
    const { handleOpenAlert } = useLayout();

    const inputInternoRef = useRef(null);
    const inputRef = inputRefExterno || inputInternoRef;
    const tablaRef = useRef(null);

    const enfocarFiltro = () => {
        // Intento síncrono primero (caso repetir Alt+F1 con modal abierto)
        // + reintentos porque el Modal usa portal y el input puede montar tarde.
        const intentar = () => {
            const el = inputRef.current;
            if (!el) return false;
            el.focus();
            el.select?.();
            return true;
        };
        if (intentar()) {
            requestAnimationFrame(() => {
                if (document.activeElement !== inputRef.current) intentar();
            });
            return;
        }
        let intentos = 0;
        const reintentar = () => {
            if (intentar()) return;
            if (intentos++ < 10) setTimeout(reintentar, 50);
        };
        requestAnimationFrame(() => setTimeout(reintentar, 0));
    };

    const persistRef = useRef({
        filtro: "",
        lista: [],
        indexSel: -1
    });

    const [filtro, setFiltro] = useState(persistRef.current.filtro);
    const [lista, setLista] = useState(persistRef.current.lista);
    const [indexSel, setIndexSel] = useState(persistRef.current.indexSel);

    useEffect(() => {
        persistRef.current = { filtro, lista, indexSel };
    }, [filtro, lista, indexSel]);

    useEffect(() => {
        if (openModal) {
            setFiltro(persistRef.current.filtro);
            setLista(persistRef.current.lista);
            setIndexSel(persistRef.current.indexSel);

            // Alt+F1 debe dejar siempre el foco con select en el filtro,
            // aunque haya una selección previa en la tabla.
            enfocarFiltro();
        }
    }, [openModal]);

    useEffect(() => {
        if (!openModal) return;

        const esAltF1 = (event) => {
            if (!event.altKey) return false;
            const key = (event.key || "").trim().toUpperCase();
            return key === "F1" || event.code === "F1" || event.keyCode === 112;
        };

        const handleKeyDownGlobal = (event) => {
            if (esAltF1(event)) {
                event.preventDefault();
                event.stopPropagation();
                enfocarFiltro();
            }
        };

        // capture=true como respaldo del handler global del padre:
        // si el foco esté en la tabla del modal, Alt+F1 regresa al filtro.
        window.addEventListener("keydown", handleKeyDownGlobal, true);
        return () => window.removeEventListener("keydown", handleKeyDownGlobal, true);
    }, [openModal]);

    const buscar = async () => {
        if (!filtro || !folioInventario) return;

        let url = apiTomaInventarioBuscarDetalle
            .replace("{folioinventario}", folioInventario)
            + "?q=" + encodeURIComponent(filtro);

        const res = await GetRequest({
            url,
            loader: "Buscando artículos en inventario"
        });

        if (res.HasError) {
            handleOpenAlert(res.message || "Error al buscar");
            return;
        }

        const data = res.result || [];

        setLista(data);

        if (data.length > 0) {
            setIndexSel(0);
            setTimeout(() => {
                tablaRef.current?.focus();
                scrollToIndex(0);
            }, 50);
        }
    };

    const scrollToIndex = (index) => {
        const container = tablaRef.current;
        const rows = container?.querySelectorAll(".row-articulo");
        if (!rows || !rows[index]) return;

        const row = rows[index];
        const rowTop = row.offsetTop;
        const rowBottom = rowTop + row.offsetHeight;
        const viewTop = container.scrollTop;
        const viewBottom = viewTop + container.clientHeight;

        if (rowBottom > viewBottom) {
            container.scrollTop = rowBottom - container.clientHeight;
        }
        if (rowTop < viewTop) {
            container.scrollTop = rowTop;
        }
    };

    const handleKeyDownInput = async (e) => {
        if (e.key === "Enter") {
            await buscar();
        }
        if (e.key === "ArrowDown") {
            if (lista.length > 0) {
                e.preventDefault();
                setIndexSel(0);
                setTimeout(() => {
                    tablaRef.current?.focus();
                    scrollToIndex(0);
                }, 10);
            }
        }
    };

    const handleKeyDownTabla = async (e) => {
        if (!lista.length) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            const next = indexSel + 1;
            if (next < lista.length) {
                setIndexSel(next);
                scrollToIndex(next);
            }
        }

        if (e.key === "ArrowUp") {
            e.preventDefault();
            if (indexSel > 0) {
                const prev = indexSel - 1;
                setIndexSel(prev);
                scrollToIndex(prev);
            } else {
                tablaRef.current?.blur();
                inputRef.current?.focus();
                inputRef.current?.select();
                return;
            }
        }

        if (e.key === "Enter") {
            const art = lista[indexSel];
            if (!art) return;
            await handleArticuloSelect(art);
            handleCloseModal();
        }

        if (e.key === "Escape") {
            handleCloseModal();
        }
    };

    const handleDoubleClickRow = async (art) => {
        await handleArticuloSelect(art);
        handleCloseModal();
    };

    const colEstilos = {
        descripcion: { width: "35%", textAlign: "left" },
        clave: { width: "20%", textAlign: "left" },
        medida: { width: "15%", textAlign: "center" },
        existencia: { width: "15%", textAlign: "center" },
        codigobarras: { width: "15%", textAlign: "left" }
    };

    const thStyle = {
        padding: "8px",
        background: "#f5f5f5",
        borderBottom: "2px solid #ddd",
        fontWeight: "bold",
        fontSize: "13px"
    };

    const headerStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        backgroundColor: "#1976d2",
        color: "#fff",
        borderRadius: "8px 8px 0 0"
    };

    const tdStyle = {
        padding: "8px",
        borderBottom: "1px solid #eee"
    };

    return (
        <Modal open={openModal} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                <div style={headerStyle}>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                        Buscar artículo en inventario
                    </Typography>
                    <IconButton onClick={handleCloseModal} sx={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </div>

                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <TextField
                        inputRef={inputRef}
                        label="Filtro (Alt+F1 para enfocar)"
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={handleKeyDownInput}
                        autoComplete="off"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 400 }}
                    />
                </div>

                <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", padding: '0 8px' }}>
                    <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={{ ...thStyle, ...colEstilos.descripcion }}>Descripción</th>
                                <th style={{ ...thStyle, ...colEstilos.clave }}>Clave</th>
                                <th style={{ ...thStyle, ...colEstilos.medida }}>Medida</th>
                                <th style={{ ...thStyle, ...colEstilos.existencia }}>Existencia</th>
                                <th style={{ ...thStyle, ...colEstilos.codigobarras }}>Código Barras</th>
                            </tr>
                        </thead>
                    </table>

                    <div
                        ref={tablaRef}
                        tabIndex={0}
                        onKeyDown={handleKeyDownTabla}
                        style={{ overflowY: "auto", flex: 1 }}
                    >
                        <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse" }}>
                            <tbody>
                                {lista.length > 0 ? (
                                    lista.map((a, i) => (
                                        <tr
                                            key={i}
                                            className="row-articulo"
                                            onClick={() => setIndexSel(i)}
                                            onDoubleClick={() => handleDoubleClickRow(a)}
                                            style={{
                                                background: i === indexSel ? "#e3f2fd" : "white",
                                                cursor: "pointer"
                                            }}
                                        >
                                            <td style={{ ...tdStyle, ...colEstilos.descripcion }}>{a.descripcion}</td>
                                            <td style={{ ...tdStyle, ...colEstilos.clave }}>{a.clavearticulo}</td>
                                            <td style={{ ...tdStyle, ...colEstilos.medida }}>{a.medida}</td>
                                            <td style={{ ...tdStyle, ...colEstilos.existencia }}>{a.existencia}</td>
                                            <td style={{ ...tdStyle, ...colEstilos.codigobarras }}>{a.codigobarras}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: "center", padding: 20 }}>
                                            {filtro ? "Sin resultados" : "Escribe y presiona Enter"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Box>
        </Modal>
    );
}
